import { Component, Input, OnInit } from '@angular/core';

import { switchMap } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { SourceGroup } from '../../../Sources/models/source-group.model';
import { SourceGroupService } from '../../../Sources/services/source-group.service';
import { JournodexSourceModel } from '@Common/Journodex/models/journodex-source.model';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { SearchCriteriaSourcesEnum } from '@api/mentions/enum/search-criteria-sources.enum';
import { searchCriteriaSourcesEnumToSourceTypeEnum as criteriaToSourceTypeMap } from '@api/mentions/enum/search-criteria-sources.enum';
import { Source } from '../../models/source.model';
import { SourceService } from '../../services/source.service';

enum SaveAsOptionEnum {
    saveAsNew,
    saveToExisting
}

@Component({
    selector: 'agpr-save-as-group-dialog',
    templateUrl: './save-as-group-dialog.component.html',
    styleUrls: ['./save-as-group-dialog.component.scss'],
    standalone: false
})
export class SaveAsGroupDialogComponent extends AbstractDialogComponent implements OnInit {
    readonly buttonTypes = ButtonTypesEnum;
    readonly saveAsOptions = SaveAsOptionEnum;

    @Input() sourceList: JournodexSourceModel[];

    showSaveAsOptions: boolean = false;
    saveAsOption: SaveAsOptionEnum = SaveAsOptionEnum.saveAsNew;
    groupName: string = '';
    groupNameErrors: string[] = [];
    groupOptions: DropdownOptionModel[] = [];
    selectedGroup: DropdownOptionModel;
    selectedSources: JournodexSourceModel[] = [];
    includeSourcesFromGroups: boolean = true;
    includedSourceGroups: JournodexSourceModel[] = [];
    includedSourceGroupModels: SourceGroup [] = [];

    readonly typeToStringMap = {
        [SearchCriteriaSourcesEnum.journalist]: '(CON)',
        [SearchCriteriaSourcesEnum.outlet]: '(OUT)',
        [SearchCriteriaSourcesEnum.urls]: '(URL)',
        [SearchCriteriaSourcesEnum.sourceGroups]: '(GRU)',
    };

    constructor(
        public coreDialogService: CoreDialogService,
        private sourceGroupService: SourceGroupService,
        private sourceService: SourceService
    ) {
        super(coreDialogService, 'AddSourcesToGroup');
    }

    ngOnInit() {
        this.includedSourceGroups = this.sourceList.filter(source => source.entityType === SearchCriteriaSourcesEnum.sourceGroups);
        if (this.includedSourceGroups.length) {
            this.showSaveAsOptions = true;
            this.groupOptions = this.includedSourceGroups.map(group => {
                return {
                    label: group.displayString,
                    value: group.entityName
                };
            });
            this.includedSourceGroups.forEach(group => {
                this.sourceGroupService.getById(Number(group.entityName)).subscribe(groupModel => {
                    this.includedSourceGroupModels.push(groupModel);
                });
            });
        } else {
            this.includeSourcesFromGroups = false;
        }
        this.selectedSources = this.sourceList.filter(source => source.entityType !== SearchCriteriaSourcesEnum.sourceGroups);
    }

    validateName() {
        this.groupNameErrors = [];

        if (!this.groupName.trim()) {
            this.groupNameErrors.push('SourceGroups.error.emptyName');

            return;
        }

        this.sourceGroupService.isNameDuplicated$(this.groupName.trim()).subscribe(isDuplicate => {
            if (isDuplicate) {
                this.groupNameErrors.push('SourceGroups.error.duplicate');
            }
        });
    }

    selectedGroupChanged() {
        const selectedGroup = this.includedSourceGroups.find(group => group.entityName === this.selectedGroup.value);
        this.removeGroup(selectedGroup);
    }

    removeSource(source: JournodexSourceModel) {
        this.selectedSources = this.selectedSources.filter(includedSource => includedSource !== source);
    }

    removeGroup(group: JournodexSourceModel) {
        this.includedSourceGroups = this.includedSourceGroups.filter(sourceGroup => sourceGroup !== group);
        this.includedSourceGroupModels = this.includedSourceGroupModels.filter(groupModel => groupModel.id !== Number(group.entityName));
        if (this.includedSourceGroups.length === 0) {
            this.includeSourcesFromGroups = false;
        }
    }

    confirm() {
        if (this.selectedSources.length === 0) {
            return;
        }

        if (this.saveAsOption === SaveAsOptionEnum.saveAsNew) {
            this.validateName();
            if (this.groupNameErrors.length > 0) {
                return;
            }

            const group = new SourceGroup();
            group.name = this.groupName;
            group.sourceIds = [];

            if (this.includeSourcesFromGroups) {
                this.includedSourceGroupModels.forEach(groupModel => group.sourceIds.push(...groupModel.sourceIds));
            }

            const sources: Source[] = this.selectedSources.map(jdexSource => {
                const source: Source = new Source();
                source.name = jdexSource.entityName.replace(/['"]+/g, '');
                source.type = criteriaToSourceTypeMap.get(jdexSource.entityType);
                return source;
            });

            this.sourceService.createBulk(sources).pipe(
                switchMap(ids => {
                    group.sourceIds.push(...ids);

                    return this.sourceGroupService.create(group);
                })
            ).subscribe((id) => {
                group.id = id;
                if (this.includeSourcesFromGroups) {
                    this.selectedSources.push(...this.includedSourceGroups);
                }
                this.closeDialog({group: group, includedSources: this.selectedSources});
            });

        } else {
            let sourceGroup: SourceGroup;

            this.sourceGroupService.getById(Number(this.selectedGroup.value)).pipe(
                switchMap(groupModel => {
                    sourceGroup = groupModel;

                    if (this.includeSourcesFromGroups) {
                        this.includedSourceGroupModels.forEach(model => sourceGroup.sourceIds.push(...model.sourceIds));
                    }

                    const sources: Source[] = this.selectedSources.map(jdexSource => {
                        const source: Source = new Source();
                        source.name = jdexSource.entityName.replace(/['"]+/g, '');
                        source.type = criteriaToSourceTypeMap.get(jdexSource.entityType);
                        return source;
                    });

                    return this.sourceService.createBulk(sources).pipe(
                        switchMap(ids => {
                            sourceGroup.sourceIds.push(...ids);

                            return this.sourceGroupService.update(sourceGroup);
                        })
                    );
                })
            ).subscribe(() => {
                if (this.includeSourcesFromGroups) {
                    this.selectedSources.push(...this.includedSourceGroups);
                }
                this.closeDialog({group: sourceGroup, includedSources: this.selectedSources});
            });
        }

    }

    cancel() {
        this.closeDialog();
    }

}
